import React, { Component } from "react";
import { style } from "typestyle";
import Input from "../../../../components/Input";

import { ActionProps, ActionCollapsedProps } from "../_ActionProps";

type ActionTranslationMatrixKey = "0" | "1" | "2" | "3" | "4" | "5";
type ActionTranslationMatrixState = {
  matrixCells: { [key: string]: string };
};

export class ActionTranslationMatrix extends Component<
  ActionProps<number[]>,
  ActionTranslationMatrixState
> {
  state = {
    matrixCells: {
      // using numbers as keys instead of array in order to make setState easier (see onCellInput method)
      "0": "",
      "1": "",
      "2": "",
      "3": "",
      "4": "",
      "5": "",
    },
  };

  render() {
    return (
      <div className={style({ width: "100%" })}>
        {this.props.label && (
          <div
            className={style({
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "24px",
              letterSpacing: "0",
              paddingBottom: "18px",
            })}
          >
            {this.props.label}
          </div>
        )}
        <table
          className={style({
            borderBottom: tableBorderStyleValue,
            borderCollapse: "collapse",
            borderSpacing: 0,
          })}
        >
          <tbody>
            <tr>
              {this.renderCell("0")}
              {this.renderCell("1")}
              {this.renderCell("2")}
            </tr>
            <tr>
              {this.renderCell("3")}
              {this.renderCell("4")}
              {this.renderCell("5")}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  renderCell(cellNumber: ActionTranslationMatrixKey) {
    return (
      <td
        className={style({
          width: "168px",
          padding: "0 12px",
          border: tableBorderStyleValue,
          borderBottom: "none",
          borderRight: "none",
          $nest: {
            "&:last-child": {
              borderRight: tableBorderStyleValue,
            },
          },
        })}
      >
        <Input
          value={this.state.matrixCells[cellNumber]}
          onChange={this.onCellInput(cellNumber)}
          className={style({
            padding: "0 !important",
            $nest: {
              "&>input": {
                border: "none",
              },
            },
          })}
        />
      </td>
    );
  }

  onCellInput = (cellNumber: string) => (value: string) => {
    const matrix: number[] | null = this.getValidMatrixFromArray(
      value.split(/\s+/),
    );
    if (matrix) {
      // user pasted matrix into a cell, we parse it and fill other cells
      // we don't use onPaste input event because it triggers before onChange and it will overwrite pasted value
      this.setState(
        {
          matrixCells: toObject(matrix.map((v: number) => `${v}`)), // need to stringify values. Input accepts only strings
        },
        () => {
          this.props.onInputSuccess(matrix);
        },
      );
      return;
    }

    this.setState(
      {
        matrixCells: {
          ...this.state.matrixCells,
          [cellNumber]: value, // using benefits of object instead of array
        },
      },
      () => {
        const matrix: number[] | null = this.getValidMatrixFromArray(
          toArray(this.state.matrixCells),
        );
        if (!matrix) {
          this.props.onInputFailure([0, 0, 0, 0, 0, 0]);
          return;
        }
        this.props.onInputSuccess(matrix);
      },
    );
  };

  getValidMatrixFromArray(stringArray: string[]) {
    if (stringArray.length !== 6) {
      return null;
    }
    // input always returns strings so we parse them to floats
    const floatValues: number[] = stringArray.map((v) => parseFloatStrict(v));
    if (floatValues.findIndex(Number.isNaN) === -1) {
      // indexOf(NaN) doesn't work. Javascript ¯\_(ツ)_/¯
      return floatValues;
    }
    return null;
  }
}

interface ActionTranslationMatrixCollapsedProps {
  customerId: string;
  siteId?: string;
}

export class ActionTranslationMatrixCollapsed extends Component<ActionTranslationMatrixCollapsedProps> {
  render() {
    const { customerId, siteId } = this.props;
    if (!siteId) {
      return "CRS edit is not available without site registration in CSU";
    }
    const crsFormUrl = `${window.location.origin}/#/customers/${customerId}/sites/${siteId}/srs`;
    return (
      <span
        className={style({
          fontSize: "14px",
          lineHeight: "20px",
        })}
      >
        Please use <a href={crsFormUrl}>CRS Configurator</a> to view and edit
        site CRS.
      </span>
    );
  }
}

const tableBorderStyleValue = "1px solid #079DF5";
const tableBorderCollapsedStyleValue = "1px solid rgba(0,0,0,0.35)";

const toObject = (ar: any[]) =>
  ar.reduce(
    (prev, curr, it) => ({
      ...prev,
      [it]: curr,
    }),
    {},
  );

const toArray = (obj: { [key: string]: any }): any[] =>
  Object.keys(obj).map((key, index) => obj[key]);

// parseFloat is not strict enough. It ignores non-numeric letters at the end of the srtring
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/parseFloat#A_stricter_parse_function
const parseFloatStrict = (value: string) => {
  if (/^(-|\+)?([0-9]+(\.[0-9]+)?|Infinity)$/.test(value)) {
    return Number(value);
  }
  return NaN;
};
